import { useCallback, useEffect, useRef, useState } from "react";
import { INPUT_MAX_CHARS, cutProse, verbOf, type ChapterEnd, type Cut, type Intent, type LookView, type Resource, type StoryView, type StreamLine, type ThreadView, type VerbKind } from "../shared/story";
import { PACE_MAX, WRITE_PACE, readPace, savePace } from "./pace";
import { api, ApiError, explain } from "./api";
import { metaOf, type Meta } from "./meta";
import { FIRST_DREAM_AT, FREE_WARN_AT, type PatronView } from "../shared/patron";
import { turnstileToken } from "./turnstile";

/**
 * The story engine every theme shares: streaming, pause-on-type, cut-in, look closer, crisis, refresh after the canon
 * pass. Themes only decide how it looks (StoryUI in, a page out).
 */

/** A short message for the screen the player lands on (App shows it: a story gone, a story deleted). */
export const toast = (message: string) => window.dispatchEvent(new CustomEvent("weftwake:toast", { detail: message }));

/** Typing holds the narrator; it writes on this long after the last keystroke if the player hasn't sent. */
export const RESUME_MS = 2000;
/**
 * How fast the narrator writes on screen: the reader's pace (pace.ts; 40 characters a second, about 7 words, unless
 * they chose otherwise), slower than the model streams, so a reply reads as being written. Whatever arrived and isn't
 * shown yet waits, and a cut keeps only what was shown.
 */
export { WRITE_PACE };
const TICK_MS = 50;
/** Holding the reply makes the hand write this many times faster; each long hold nudges the player's own pace up. */
const HURRY = 4;
const words = (s: string) => s.split(/\s+/).filter(Boolean).length;

/** How long the cut-away rest of a reply stays on screen, unravelling. */
const UNRAVEL_MS = 4600; // the longest theme erasure (Manuscript: strike, then lift), with a little to spare

export interface Shown {
  n: number; input: string; prose: string; status: "streaming" | "done" | "cut"; intent: Intent | null; tension?: number;
  /** Words the player cut away unseen. */
  unwoven?: number;
  /** A dormant thread that came back in this turn. */
  callback?: string;
  /** A thread the player pulled in this turn. */
  pulled?: string;
  /** A chapter ended in this turn; the story ended in this turn. */
  chapterEnd?: ChapterEnd;
  storyEnd?: { question: string; outcome: "earned" | "turned" | "lost" };
  /** The chapter's moment of decision arrived in this turn. */
  decision?: true;
  /** Facts this line made true; the command that shaped it. */
  kept?: string[];
  verb?: VerbKind;
  /** The narrator's voice turned with the story at this turn. */
  voiceShift?: { from: string; to: string };
}
export interface Live { n: number; input: string; intent: Intent | null; tension: number; callback?: string }

export interface StoryUI {
  /** null until the first reply names the new story. */
  id: string | null;
  view: StoryView | null;
  /** Settled turns, oldest first (the live one is separate). */
  turns: Shown[];
  /** The reply being written right now (or paused), with the text the player has seen so far. */
  live: (Live & { text: string; streaming: boolean; paused: boolean }) | null;
  /** The player's line, sent, waiting for the narrator's first words. */
  pending: string | null;
  looks: Record<number, LookView[]>;
  resources: Resource[] | null;
  notice: string | null;
  loadFailed: boolean;
  /** A story is being opened (its address is known, its text isn't here yet). */
  loading: boolean;
  /** A look closer on its way (the name tapped, and in which turn). */
  looking: { n: number; thing: string } | null;
  draft: string;
  /** V2, two pens: Ink (what you do) or Thread (what you make true: the line goes as /weave, then the pen is Ink again). */
  pen: "ink" | "thread";
  setPen(p: "ink" | "thread"): void;
  placeholder: string;
  busy: boolean;
  onType(value: string): void;
  send(text: string): void;
  /** Puts text in the line, ready to edit and send (the "/" menu, "go there" in the map). */
  fill(text: string): void;
  /** Threads you can pull: tap an open thread and the narrator brings it back now (a move of its own). */
  pull(thread: ThreadView): void;
  look(turn: number, name: string): void;
  /** V2, words as things: put a name in the pocket or take it out (no turn); write a name into the line, after what's there. */
  pocket(thing: string, put: "in" | "out"): void;
  writeWith(words: string): void;
  /** Opening truths for a story not yet begun: up to three things true here, and one fear. */
  scene: { truths: string[]; fear: string };
  /** Begins a story from an opening someone passed on: its line, truths and narrator, as they set it. */
  beginSeed(seed: { code: string; opening: string; truths: string[] }): void;
  okay(): void;
  reload(): void;
  remove(): void;
  /** Beta: run tonight's dream now (null when the preview hasn't switched it on, or no story yet). */
  dreamNow: (() => void) | null;
  dreaming: boolean;
  /** Free turns left, when it's worth saying (payments open, not a Patron, FREE_WARN_AT or fewer); else null. */
  freeLeft: number | null;
  /** The reply the player just cut: the part they never saw, unravelling for a moment (the cut as a ceremony). */
  unravel: { n: number; text: string } | null;
  /** Hold the reply to make the hand write faster (the reader drives the hand). */
  hurry(on: boolean): void;
  hurrying: boolean;
}

export function useStory(initialId: string | null, o: {
  nav(path: string, replace?: boolean): void; onStart?(): void;
  /** A line that is a request to the app ("privacy", "delete everything"…), never sent to the narrator. */
  onMeta?(m: Meta): void;
}): StoryUI {
  const { nav, onStart, onMeta } = o;
  const [id, setId] = useState(initialId);
  /** Opening truths (co-creation step 3): set on the home screen, sent with the first line. */
  const sceneRef = useRef<{ truths: string[]; fear: string; seed?: string }>({ truths: ["", "", ""], fear: "" });
  const [view, setView] = useState<StoryView | null>(null);
  const [turns, setTurns] = useState<Shown[]>([]);
  const [live, setLive] = useState<Live | null>(null);
  const [received, setReceived] = useState("");
  const [streaming, setStreaming] = useState(false);
  // How much of the live reply is on screen. Typing stops it (paused); it stays stopped while the player types and for
  // RESUME_MS after their last keystroke, then writes on at WRITE_PACE, so a held-back reply never lands all at once.
  const [cursor, setCursor] = useState(0);
  const [paused, setPausedState] = useState(false);
  const pausedRef = useRef(false);
  const setPaused = (p: boolean) => { pausedRef.current = p; setPausedState(p); };
  const resumeTimer = useRef<number | undefined>(undefined);
  const paceRef = useRef(readPace());
  const [hurrying, setHurrying] = useState(false);
  const hurryStart = useRef(0);
  const [unravel, setUnravel] = useState<{ n: number; text: string } | null>(null);
  const unravelTimer = useRef<number | undefined>(undefined);
  const [draft, setDraft] = useState("");
  const [pen, setPen] = useState<"ink" | "thread">("ink");
  const [pending, setPending] = useState<string | null>(null);
  const [looks, setLooks] = useState<Record<number, LookView[]>>({});
  const [resources, setResources] = useState<Resource[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [canDream, setCanDream] = useState(false);
  const [dreaming, setDreaming] = useState(false);
  const [allowance, setAllowance] = useState<PatronView | null>(null);
  const firstDreamAsked = useRef(false);
  /**
   * After a story loads or a turn settles: how many free turns are left, and whether the first dream is due (at
   * FIRST_DREAM_AT free turns or the end of the first chapter, for a player who isn't a Patron and hasn't dreamt).
   */
  const checkAllowance = useCallback(async (sid: string, story: StoryView) => {
    const v = await api.patron().catch(() => null);
    if (!v) return;
    setAllowance(v);
    const due = !v.patron && v.dreamsUsed === 0 && (v.free.used >= FIRST_DREAM_AT || story.turns.some((t) => t.chapterEnd));
    if (!due || firstDreamAsked.current || story.dreamt) return;
    firstDreamAsked.current = true;
    setDreaming(true);
    setNotice("The loom is dreaming your story for the first time… (a few seconds)");
    api.firstDream(sid)
      .then((r) => { setView(r.story); setNotice(null); })
      .catch(() => setNotice(null))
      .finally(() => setDreaming(false));
  }, []);
  // A reply to a morning letter waits in the line, once, for the player to send (or change).
  const waitingShown = useRef<string | null>(null);
  useEffect(() => {
    const w = view?.waiting;
    if (!w || waitingShown.current === w || live || pending) return;
    waitingShown.current = w;
    setDraft((d) => d.trim() ? d : w);
    setNotice("Your reply to the morning letter is in the line. Send it to go on.");
  }, [view?.waiting]);
  // The app's settings (Turnstile key, beta switches). Starting a story waits for them, however fast the first line is.
  const config = useRef<ReturnType<typeof api.config> | null>(null);
  useEffect(() => {
    const c = api.config();
    config.current = c;
    c.then((v) => setCanDream(v.dreamNow)).catch(() => {});
  }, []);
  const abortRef = useRef<AbortController | null>(null);
  // What the player has seen of the latest reply, and how much of it exists: a gap means a cut.
  const lastRef = useRef<{ n: number; generated: number; shown: number } | null>(null);
  const pullRef = useRef<{ id: string; name: string } | null>(null);
  const [looking, setLooking] = useState<{ n: number; thing: string } | null>(null);
  const refreshTimer = useRef<number | undefined>(undefined);

  const shownChars = Math.min(cursor, received.length);
  const writing = !!live && !paused && (streaming || shownChars < received.length);

  // The writing hand: the reply comes onto the screen at WRITE_PACE, however fast it arrives.
  useEffect(() => {
    if (!live || paused || cursor >= received.length) return;
    const step = Math.max(1, Math.round(paceRef.current * (hurrying ? HURRY : 1) * TICK_MS / 1000));
    const t = window.setTimeout(() => setCursor((c) => Math.min(received.length, c + step)), TICK_MS);
    return () => window.clearTimeout(t);
  }, [live, paused, cursor, received, hurrying]);

  // The stream has ended and everything is on screen: the reply settles into the story.
  useEffect(() => {
    if (live && !streaming && !paused && received && cursor >= received.length && !abortRef.current) {
      setLive(null); setReceived(""); setCursor(0);
    }
  }, [live, streaming, paused, received, cursor]);
  useEffect(() => () => window.clearTimeout(resumeTimer.current), []);

  const refresh = useCallback(async (sid: string) => {
    try {
      const r = await api.story(sid);
      setView(r.story);
      setResources(r.resources);
      setTurns((cur) => (abortRef.current ? cur : r.story.turns));
      setLoadFailed(false);
      void checkAllowance(sid, r.story);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        nav("/", true);
        toast("That story isn't here. It may have been deleted, or it belongs to another account (sign in to see it).");
      }
      else setLoadFailed(true);
    }
  }, [nav, checkAllowance]);

  useEffect(() => { if (initialId) void refresh(initialId); }, [initialId, refresh]);
  useEffect(() => () => window.clearTimeout(refreshTimer.current), []);

  // Leaving mid-reply: tell the server how much was seen.
  useEffect(() => {
    const leave = () => {
      const last = lastRef.current;
      if (id && last && live && last.shown < last.generated) api.beaconCut(id, { turn: last.n, chars: last.shown });
    };
    // Only on a real leave: a hidden tab keeps receiving, and the player may come back and read the rest. If the page
    // dies without a pagehide, the whole reply counts (spec, barge-in rule 6).
    window.addEventListener("pagehide", leave);
    return () => window.removeEventListener("pagehide", leave);
  }, [id, live]);

  useEffect(() => {
    if (lastRef.current && live) lastRef.current = { ...lastRef.current, generated: received.length, shown: shownChars };
  }, [received, shownChars, live]);

  async function send(text: string, pull?: string) {
    const input = text.trim().slice(0, INPUT_MAX_CHARS);
    if (!input || (pending && !live)) return;
    const meta = pull ? null : metaOf(input);
    if (meta === "finale") { setDraft(""); askFinale(); return; }
    if (meta && onMeta) { setDraft(""); onMeta(meta); return; }
    if (/^\//.test(input) && !verbOf(input)) { setNotice("Commands: /ask a question, /elsewhere somewhere, /trouble, /clock until…, /flashback something you did, /weave something true, or /journal. Type help for the short list."); return; }
    setNotice(null);
    // Cut in: stop the reply where the player stopped reading.
    let cut: Cut | null = null;
    const last = lastRef.current;
    if (last && last.shown < last.generated) cut = { turn: last.n, chars: last.shown };
    abortRef.current?.abort();
    abortRef.current = null;
    if (live) {
      // Same rule as the server's canon, so the page doesn't change when it reloads.
      const c = cutProse(received, cut ? cut.chars : received.length);
      const rest = c.cut ? received.slice(c.prose.replace(/—$/u, "").length) : "";
      const unwoven = words(rest);
      setTurns((cur) => [...cur.filter((t) => t.n !== live.n), { ...live, prose: c.prose, status: c.cut ? "cut" : "done", ...(unwoven ? { unwoven } : {}) }]);
      if (unwoven) {
        setUnravel({ n: live.n, text: rest });
        window.clearTimeout(unravelTimer.current);
        unravelTimer.current = window.setTimeout(() => setUnravel(null), UNRAVEL_MS);
      }
    }
    setLive(null);
    setReceived("");
    setStreaming(false);
    setPaused(false);
    window.clearTimeout(resumeTimer.current);
    setCursor(0);
    setDraft("");
    setPen("ink");
    setPending(input);
    lastRef.current = null;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let sid = id;
    let current: Live | null = null;
    let text2 = "";
    const onLine = (l: StreamLine) => {
      if (l.t === "turn") {
        sid = l.story;
        // A new story: keep this component (the stream is running in it) and just give the page its address.
        if (!id) { setId(l.story); history.replaceState(null, "", `/s/${l.story}`); onStart?.(); }
        current = { n: l.n, input, intent: l.intent, tension: l.tension, ...(l.callback ? { callback: l.callback } : {}) };
        setLive(current);
        setStreaming(true);
        setPending(null);
        lastRef.current = { n: l.n, generated: 0, shown: 0 };
      } else if (l.t === "prose") {
        text2 += l.text;
        setReceived(text2);
      } else if (l.t === "crisis") {
        setResources(l.resources);
        setPending(null);
      } else if (l.t === "error") {
        // A command used up for now costs nothing: say why, and give the line back.
        const used: Record<string, string> = {
          trouble_used: "You've asked for trouble once this chapter. The world will bring its own.",
          flashback_used: "One flashback a chapter: you've had this chapter's.",
          clocks_full: "Two clocks of your own are already running. Let one run out first.",
          weaves_used: "No threads left for now: three a chapter, one every ten turns between chapters. Ink still writes.",
        };
        if (used[l.error]) { setNotice(used[l.error]!); setDraft(input.replace(/^\/weave\s+/i, "")); setPending(null); }
        else setNotice(l.error === "refusal" ? "The narrator won't take the story there. Try another way." : "The narrator lost the thread. Try again.");
      }
    };
    try {
      if (sid) await api.turn(sid, input, cut, onLine, ctrl.signal, pull);
      else {
        const key = (await (config.current ?? api.config()).catch(() => null))?.turnstileSiteKey ?? null;
        await api.begin(input, onLine, ctrl.signal, await turnstileToken(key), sceneRef.current);
      }
      if (abortRef.current !== ctrl) return;
      abortRef.current = null;
      setStreaming(false);
      const done = current as Live | null;
      if (done && text2) {
        // Kept live until it is all on screen (the player may have paused it); then it settles (effect above).
        setTurns((cur) => [...cur.filter((t) => t.n !== done.n), { ...done, prose: text2, status: "done" }]);
      } else if (done) {
        setLive(null);
      }
      if (sid) {
        window.clearTimeout(refreshTimer.current);
        // The canon pass (new names, threads, the map, the soundscape) lands a few seconds after the reply; a slow
        // one is caught by a second look.
        refreshTimer.current = window.setTimeout(() => {
          void refresh(sid!);
          refreshTimer.current = window.setTimeout(() => void refresh(sid!), 9000);
        }, 3500);
      }
    } catch (e) {
      if (ctrl.signal.aborted) return;
      abortRef.current = null;
      setStreaming(false);
      setPending(null);
      setNotice(!sid && e instanceof ApiError && e.code === "quota"
        ? "That's three new stories for today. Pick up one you've begun, or start another tomorrow."
        : explain(e));
      if (!current) setDraft(input);
      // The free chapter ended, or the month's turns: the Patron sheet says why and what's next.
      if (e instanceof ApiError && (e.code === "patron_needed" || e.code === "allocation_used" || e.code === "network_free_used")) {
        window.dispatchEvent(new CustomEvent("weftwake:patron", { detail: { reason: e.code } }));
      }
    }
  }

  function onType(value: string) {
    setDraft(value);
    if (!live || (!streaming && shownChars >= received.length)) return;
    // Any keystroke (typing or deleting) holds the narrator; it writes on RESUME_MS after the last one.
    setPaused(true);
    window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => setPaused(false), RESUME_MS);
  }

  /** The player asks for an ending: the current or next chapter becomes the finale. */
  function askFinale() {
    if (!id) return;
    api.finale(id).then(() => {
      setNotice("The story will find its ending in the next few turns.");
      void refresh(id);
    }, (e) => setNotice(explain(e)));
  }
  useEffect(() => {
    const on = () => askFinale();
    window.addEventListener("weftwake:finale", on);
    return () => window.removeEventListener("weftwake:finale", on);
  });

  async function look(n: number, thing: string) {
    if (!id) return;
    setLooking({ n, thing });
    try {
      const r = await api.look(id, thing);
      setLooks((cur) => ({ ...cur, [n]: [...(cur[n] ?? []).filter((l) => l.thing !== r.look.thing), r.look] }));
      void refresh(id);
    } catch (e) {
      setNotice(e instanceof ApiError && e.code === "refusal" ? "The loom won't look closer at that. Try something else in the scene." : explain(e));
    } finally {
      setLooking((cur) => (cur?.thing === thing ? null : cur));
    }
  }

  return {
    id, view, turns: live ? turns.filter((t) => t.n !== live.n) : turns,
    live: live ? { ...live, text: received.slice(0, shownChars), streaming: writing, paused } : null,
    pending, looks, resources, notice, loadFailed, draft, looking, pen, setPen,
    loading: !!id && !view && !loadFailed,
    placeholder: live && (streaming || shownChars < received.length) ? "Type to stop the narrator and cut in" : id ? "What do you do?" : "Begin your story…",
    busy: !!pending && !live,
    onType,
    // A pull put in the line by a tap goes as a pull if the player sends it as it is (they may edit or delete it).
    send: (t) => { const p = pullRef.current; pullRef.current = null; void send(t, p && t.trim().startsWith(`↺ ${p.name}`) ? p.id : undefined); },
    look: (n, name) => void look(n, name),
    pocket: (thing, put) => {
      if (!id) return;
      api.pocket(id, thing, put).then((r) => setView(r.story), (e) => setNotice(e instanceof ApiError && e.code === "pocket_full"
        ? "Your pocket is full: twelve names. Take one out first with the × beside it." : explain(e)));
    },
    writeWith: (words) => {
      setDraft((d) => (d.trim() ? `${d.replace(/\s+$/, "")} ${words}` : words));
      requestAnimationFrame(() => { const el = document.getElementById("lm-input") as HTMLTextAreaElement | null; el?.focus(); el?.setSelectionRange(el.value.length, el.value.length); });
    },
    scene: sceneRef.current,
    beginSeed: (seed) => {
      Object.assign(sceneRef.current, { truths: [...seed.truths], fear: "", seed: seed.code });
      void send(seed.opening);
    },
    fill: (text) => {
      pullRef.current = null;
      setDraft(text);
      requestAnimationFrame(() => { const el = document.getElementById("lm-input") as HTMLTextAreaElement | null; el?.focus(); el?.setSelectionRange(el.value.length, el.value.length); });
    },
    // Tapping a thread doesn't spend a turn: it puts "↺ name" in the line, to send (or not) like any move.
    pull: (t) => {
      if (!id || t.state !== "open") return;
      pullRef.current = { id: t.id, name: t.name };
      setDraft(`↺ ${t.name} `);
      requestAnimationFrame(() => { const el = document.getElementById("lm-input") as HTMLTextAreaElement | null; el?.focus(); el?.setSelectionRange(el.value.length, el.value.length); });
    },
    okay: () => { if (id) void api.okay(id).catch(() => {}); setResources(null); },
    reload: () => { if (id) void refresh(id); },
    // Asks in the app's own sheet (App.tsx), never the browser's confirm().
    remove: () => { if (id) window.dispatchEvent(new CustomEvent("weftwake:delete-story", { detail: { id, title: view?.title ?? "this story" } })); },
    dreaming,
    freeLeft: allowance && allowance.open && !allowance.patron && allowance.free.limit - allowance.free.used <= FREE_WARN_AT
      ? Math.max(0, allowance.free.limit - allowance.free.used) : null,
    unravel,
    hurrying,
    hurry: (on: boolean) => {
      if (on) { hurryStart.current = Date.now(); setHurrying(true); return; }
      if (!hurryStart.current) return;
      // A long hold says this reader reads faster: settle their own pace a little higher (kept on this device).
      if (Date.now() - hurryStart.current > 800 && paceRef.current < PACE_MAX) {
        paceRef.current = Math.min(PACE_MAX, paceRef.current + 5);
        savePace(paceRef.current);
      }
      hurryStart.current = 0;
      setHurrying(false);
    },
    dreamNow: canDream && id && !dreaming ? () => {
      setDreaming(true);
      setNotice("The loom is dreaming this story… (a few seconds)");
      api.dreamNow(id)
        .then((r) => { setView(r.story); setNotice(`The loom dreamt.${r.plan.length ? ` Something may come back: ${r.plan.join(", ")}.` : ""}`); window.setTimeout(() => void refresh(id), 4000); })
        .catch((e) => setNotice(explain(e)))
        .finally(() => setDreaming(false));
    } : null,
  };
}
